import { AI_CAPTURE_MAX_IMAGE_LENGTH } from '@repo/lib';
import { cdnUrl, imagesBucketUrl } from '../../services/env';
import logException from '../../services/logException';
import { pageKey, themesKey } from '../../lib/queryKeys';

export const CAPTURE_MAX_WIDTH = { desktop: 1280, mobile: 390 };
export const CAPTURE_MAX_HEIGHT = 4000;
const SETTLE_TIMEOUT_MS = 5000;
const RENDER_SETTLE_MS = 300;
const ASSET_TIMEOUT_MS = 8000;

const targets = new Map();

// The page editor's preview registers here, so a capture asked for on a stream that outlived it fails at once.
export function registerCaptureTarget(pageId, target) {
  targets.set(pageId, target);
  return () => {
    if (targets.get(pageId) === target) targets.delete(pageId);
  };
}

const wait = ms => new Promise(resolve => setTimeout(resolve, ms));
const nextPaint = () =>
  new Promise(resolve => requestAnimationFrame(() => requestAnimationFrame(resolve)));

// The AI's writes refetch the page and its theme; the screenshot waits for those, so it shows the saved page.
async function writesSettled(queryClient, pageId, sellerId) {
  const deadline = Date.now() + SETTLE_TIMEOUT_MS;
  const fetching = () =>
    queryClient.isFetching({ queryKey: pageKey(pageId) }) +
    queryClient.isFetching({ queryKey: themesKey(sellerId) });
  while (fetching() > 0 && Date.now() < deadline) await wait(100);
}

function blobToDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

// The image CDN sends no CORS headers, so its images would come out blank; the bucket behind it does send them.
export async function fetchFromImagesBucket(url) {
  if (!cdnUrl || !imagesBucketUrl || !url.startsWith(cdnUrl)) return false;
  try {
    const response = await fetch(url.replace(cdnUrl, imagesBucketUrl).split('?')[0], {
      mode: 'cors',
    });
    if (!response.ok) return false;
    return await blobToDataUrl(await response.blob());
  } catch {
    return false;
  }
}

const isClear = color => !color || color === 'transparent' || /^rgba\(.*,\s*0\)$/.test(color);

// The option paints the copy's root too, so it is the colour the seller sees behind the page anyway.
function backgroundBehind(element) {
  for (let node = element; node; node = node.parentElement) {
    const color = getComputedStyle(node).backgroundColor;
    if (!isClear(color)) return color;
  }
  return '#ffffff';
}

async function shoot(page, device) {
  const width = page.clientWidth;
  const fullHeight = page.scrollHeight;
  const height = Math.min(fullHeight, CAPTURE_MAX_HEIGHT);
  const { domToJpeg } = await import('modern-screenshot');
  const take = quality =>
    domToJpeg(page, {
      width,
      height,
      quality,
      scale: Math.min(1, CAPTURE_MAX_WIDTH[device] / width),
      backgroundColor: backgroundBehind(page),
      // The preview scrolls inside a fixed height; the copy is drawn at its full height.
      style: { maxHeight: 'none', overflow: 'hidden' },
      fetchFn: fetchFromImagesBucket,
      timeout: ASSET_TIMEOUT_MS,
    });

  let image = await take(0.7);
  if (image.length > AI_CAPTURE_MAX_IMAGE_LENGTH) image = await take(0.4);
  if (image.length > AI_CAPTURE_MAX_IMAGE_LENGTH) return null;
  return { image, width, height, fullHeight };
}

export async function capturePreview({ pageId, sellerId, device, queryClient }) {
  const target = targets.get(pageId);
  if (!target) return null;

  await writesSettled(queryClient, pageId, sellerId);
  target.dispatch({ type: 'startCapture', value: device });
  try {
    await nextPaint();
    await wait(RENDER_SETTLE_MS);
    if (targets.get(pageId) !== target) return null;
    const page = [...(target.containerRef.current?.children ?? [])].find(child =>
      child.classList.contains('cp_page'),
    );
    // Below the lg breakpoint the preview stays mounted but hidden unless it is fullscreen.
    if (!page || page.clientWidth === 0) return null;
    await document.fonts?.ready;
    return await shoot(page, device);
  } finally {
    target.dispatch({ type: 'endCapture' });
  }
}

let queue = Promise.resolve();

// One at a time: each capture switches the preview's device and puts the seller's back.
export function answerCapture({ requestId, device, post, ...where }) {
  const run = queue.then(async () => {
    const shot = Object.hasOwn(CAPTURE_MAX_WIDTH, device)
      ? await capturePreview({ ...where, device }).catch(error => {
          logException(error);
          return null;
        })
      : null;
    // With no image the API tells the AI it couldn't look, instead of waiting out its timeout.
    await post({ requestId, ...shot }).catch(() => undefined);
  });
  queue = run;
  return run;
}
